import { useRef, useState } from 'react';
import { FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import {
  ConfirmationDialog,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { useTaskDocuments } from './useTaskDocuments';

interface TaskDocumentsPopoverProps {
  taskId: string;
  /** Owning patient, forwarded so a successful upload also refreshes the patient
   * page's Documents-count badge (via patientUploadTick). */
  patientId?: string;
  initialCount?: number;
  /**
   * Human-readable target for the upload progress toast ("{task} · {patient}").
   * Shown so a background upload is identifiable after the popover closes or the
   * user navigates away. Falls back to a generic label when omitted.
   */
  targetLabel?: string;
  /**
   * Called after the attached-document set changes (upload or delete). The parent
   * uses this to refresh its cached `document_count` for this task, so the badge
   * stays correct when the popover unmounts/remounts (e.g. on a tab switch) and
   * falls back to `initialCount`.
   */
  onDocumentsChanged?: () => void;
  /**
   * Hide upload and delete. Set for schedule-locked rows: a signed misc task's
   * documents are bound into its signed snapshot, so the server refuses changes
   * and offering them would only produce a "please try again" that never can.
   */
  readOnly?: boolean;
}

export default function TaskDocumentsPopover({
  taskId,
  patientId,
  initialCount,
  targetLabel,
  onDocumentsChanged,
  readOnly = false,
}: TaskDocumentsPopoverProps) {
  const [open, setOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    documents,
    loading,
    deleting,
    hasFetched,
    loadFailed,
    fetchDocuments,
    openDocument,
    uploadDocument,
    deleteDocument,
  } = useTaskDocuments(taskId, onDocumentsChanged, { patientId, targetLabel });

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && !hasFetched) {
      void fetchDocuments();
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteId) return;
    await deleteDocument(pendingDeleteId);
    setPendingDeleteId(null);
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so re-selecting the same file fires onChange again.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (!file) return;

    // Hand the upload to the background manager and step out of the way: the
    // popover closes and the user can navigate freely while a persistent toast
    // tracks progress. On completion the hook refreshes the list + count.
    uploadDocument(file);
    setOpen(false);
  };

  const badgeCount = hasFetched ? documents.length : (initialCount ?? 0);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className='relative rounded p-1 hover:bg-muted'
          onClick={e => e.stopPropagation()}
        >
          <Paperclip className='h-4 w-4 text-muted-foreground' />
          {badgeCount > 0 ? (
            <span className='absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-blue-500 text-[9px] font-medium text-white'>
              {badgeCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-0' onClick={e => e.stopPropagation()}>
        <div className='border-b px-3 py-2'>
          <p className='text-xs font-medium text-muted-foreground'>Documents</p>
        </div>
        <div className='max-h-48 overflow-y-auto'>
          {loading ? (
            <p className='px-3 py-2 text-xs text-muted-foreground'>
              Loading...
            </p>
          ) : loadFailed ? (
            // "No documents attached" would misreport a failed load as an empty
            // task, so say what happened and offer the retry.
            <div className='flex items-center justify-between gap-2 px-3 py-2'>
              <p className='text-xs text-destructive'>
                Couldn&apos;t load documents.
              </p>
              <button
                className='shrink-0 text-xs text-blue-600 hover:text-blue-700'
                onClick={() => void fetchDocuments()}
              >
                Retry
              </button>
            </div>
          ) : documents.length === 0 ? (
            <p className='px-3 py-2 text-xs text-muted-foreground'>
              No documents attached
            </p>
          ) : (
            documents.map(doc => (
              <div
                key={doc.id}
                className='group flex w-full items-center gap-2 px-3 py-1.5 hover:bg-muted/50'
              >
                <button
                  className='flex min-w-0 flex-1 items-center gap-2 text-left text-sm'
                  onClick={() => void openDocument(doc.id)}
                >
                  <FileText className='h-3.5 w-3.5 shrink-0 text-blue-500' />
                  <span className='truncate'>{doc.file_name}</span>
                </button>
                {!readOnly && (
                  <button
                    className='shrink-0 opacity-0 transition-opacity group-hover:opacity-100'
                    onClick={e => {
                      e.stopPropagation();
                      setPendingDeleteId(doc.id);
                    }}
                    aria-label={`Delete ${doc.file_name}`}
                  >
                    <Trash2 className='h-3.5 w-3.5 text-destructive' />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
        {!readOnly && (
          <div className='border-t px-3 py-2'>
            <input
              ref={fileInputRef}
              type='file'
              className='hidden'
              onChange={handleUpload}
              accept='.pdf,.png,.jpg,.jpeg,.doc,.docx'
            />
            <button
              className='flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700'
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className='h-3 w-3' />
              Upload document
            </button>
          </div>
        )}
      </PopoverContent>
      <ConfirmationDialog
        open={pendingDeleteId !== null}
        title='Delete document'
        message='Are you sure you want to delete this document? This action cannot be undone.'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        loading={deleting}
        onAccept={() => void handleDeleteConfirm()}
        onCancel={() => setPendingDeleteId(null)}
      />
    </Popover>
  );
}
